import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import type { CalendarioDTO, CalendarioEventoDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { CALENDAR_PATH, errorMessage, formatLongMonthYear, monthNames, weekdayNames } from '../common/calendar-common'

const LOAD_ERROR = 'No se pudo cargar el calendario.'
const YEAR_SPAN = 2

export type CalendarEventType = 'birthday' | 'renewal' | 'collection'
export type CalendarFilter = 'all' | CalendarEventType

export const CALENDAR_FILTERS: Array<{ id: CalendarFilter; label: string }> = [
    { id: 'all', label: 'Todos' },
    { id: 'birthday', label: 'Cumpleaños' },
    { id: 'renewal', label: 'Renovaciones' },
    { id: 'collection', label: 'Cobranza' },
]

const EVENT_ORDER: Record<CalendarEventType, number> = {
    birthday: 0,
    renewal: 1,
    collection: 2,
}

export interface CalendarEventView {
    id: string
    type: CalendarEventType
    text: string
    openable: boolean
    clienteId: number | null
    polizaId: number | null
}

export interface CalendarDayView {
    key: string
    inMonth: boolean
    today: boolean
    dayNumber: string | null
    weekday: string | null
    events: CalendarEventView[]
}

const monthIso = (year: number, month: number) =>
    `${year}-${String(month).padStart(2, '0')}-01`

const eventText = (event: CalendarioEventoDTO) =>
    String(event.message || '').replace(/\s+/g, ' ').trim()

const toEvent = (event: CalendarioEventoDTO, index: number): CalendarEventView | null => {
    if (event.type !== 'birthday' && event.type !== 'renewal' && event.type !== 'collection') return null
    if (!event.date) return null
    const clienteId = event.clienteId ?? null
    const polizaId = event.polizaId ?? null
    return {
        id: `${event.type}-${event.date}-${clienteId ?? 0}-${polizaId ?? 0}-${event.reciboId ?? 0}-${index}`,
        type: event.type,
        text: eventText(event) || 'Evento',
        openable: event.type === 'birthday' ? clienteId != null : polizaId != null,
        clienteId,
        polizaId,
    }
}

const buildDays = (year: number, month: number, events: CalendarioEventoDTO[]): CalendarDayView[] => {
    const start = DateTime.local(year, month, 1)
    const daysInMonth = start.daysInMonth ?? 0
    const names = weekdayNames('long')
    const today = DateTime.now().startOf('day')
    const byDate = new Map<string, CalendarEventView[]>()

    events.forEach((event, index) => {
        const view = toEvent(event, index)
        if (!view || !event.date) return
        const bucket = byDate.get(event.date) ?? []
        bucket.push(view)
        byDate.set(event.date, bucket)
    })

    const days: CalendarDayView[] = []
    for (let pad = 1; pad < start.weekday; pad += 1) {
        days.push({ key: `pad-start-${pad}`, inMonth: false, today: false, dayNumber: null, weekday: null, events: [] })
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
        const date = DateTime.local(year, month, day)
        const iso = date.toISODate() ?? ''
        const dayEvents = [...(byDate.get(iso) ?? [])].sort(
            (left, right) => EVENT_ORDER[left.type] - EVENT_ORDER[right.type] || left.text.localeCompare(right.text, 'es'),
        )
        days.push({
            key: iso,
            inMonth: true,
            today: date.hasSame(today, 'day'),
            dayNumber: String(day),
            weekday: names[date.weekday - 1] ?? null,
            events: dayEvents,
        })
    }

    const endWeekday = DateTime.local(year, month, daysInMonth).weekday
    for (let pad = endWeekday + 1; pad <= 7; pad += 1) {
        days.push({ key: `pad-end-${pad}`, inMonth: false, today: false, dayNumber: null, weekday: null, events: [] })
    }

    return days
}

export const useCalendarViewModel = () => {
    const history = useHistory()
    const now = DateTime.now()
    const [year, setYear] = useState<number>(now.year)
    const [month, setMonth] = useState<number>(now.month)
    const [type, setType] = useState<CalendarFilter>('all')
    const [compact, setCompact] = useState(false)
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [events, setEvents] = useState<CalendarioEventoDTO[]>([])
    const [polizaId, setPolizaId] = useState<number | null>(null)
    const [clienteId, setClienteId] = useState<number | null>(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const start = DateTime.local(year, month, 1)
                const inicio = start.toISODate()
                const fin = start.endOf('month').toISODate()
                const response = await APIInvoke.invokeGET(`/calendario?inicio=${inicio}&fin=${fin}&type=${type.toUpperCase()}`)
                const payload: WrapperResponse<CalendarioDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setEvents(payload.body.eventos || [])
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [year, month, type, reloadToken])

    const minMonth = now.minus({ years: YEAR_SPAN }).startOf('month')
    const maxMonth = now.plus({ years: YEAR_SPAN }).startOf('month')
    const cursor = DateTime.local(year, month, 1)
    const days = useMemo(() => buildDays(year, month, events), [year, month, events])
    const visibleDays = compact ? days.filter((day) => day.inMonth && day.events.length > 0) : days
    const totalEvents = days.reduce((sum, day) => sum + day.events.length, 0)

    const shiftMonth = (delta: number) => {
        const next = cursor.plus({ months: delta })
        if (next < minMonth || next > maxMonth) return
        setYear(next.year)
        setMonth(next.month)
    }

    const openEvent = (event: CalendarEventView) => {
        if (event.type === 'birthday' && event.clienteId != null) {
            setPolizaId(null)
            setClienteId(event.clienteId)
            return
        }
        if (event.polizaId != null) {
            setClienteId(null)
            setPolizaId(event.polizaId)
        }
    }

    return {
        year,
        month,
        months: monthNames(),
        years: Array.from({ length: YEAR_SPAN * 2 + 1 }, (_, index) => now.year - YEAR_SPAN + index),
        weekdays: weekdayNames('short'),
        monthLabel: formatLongMonthYear(monthIso(year, month)) || '',
        type,
        compact,
        loading,
        initialLoadDone,
        error,
        days: visibleDays,
        totalEvents,
        isCurrentMonth: cursor.hasSame(now, 'month'),
        canPrev: cursor > minMonth,
        canNext: cursor < maxMonth,
        polizaId,
        clienteId,
        reload: () => setReloadToken((token) => token + 1),
        changeMonth: (value: string) => setMonth(Number(value)),
        changeYear: (value: string) => setYear(Number(value)),
        changeType: (value: CalendarFilter) => setType(value),
        changeCompact: (value: boolean) => setCompact(value),
        clearFilters: () => {
            setType('all')
            setCompact(false)
        },
        goToday: () => {
            setYear(now.year)
            setMonth(now.month)
        },
        prevMonth: () => shiftMonth(-1),
        nextMonth: () => shiftMonth(1),
        openEvent,
        closePoliza: () => setPolizaId(null),
        closeCliente: () => setClienteId(null),
        editCliente: (id: number) => history.push({ pathname: `/clientes/${id}/edit`, state: { backUrl: CALENDAR_PATH } }),
    }
}
